// BusinessValueListItem.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SECURITY_PILLARS } from '../../../constants';
import { ChevronDown, ChevronRight, Edit2, Trash2, Plus, Save, X, Package, Server, Users, MapPin, FileText, Code, Network, Home, Unlink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import DeleteConfirmationModal from '../../common/DeleteConfirmationModal';

const BusinessValueListItem = ({
  value,
  expandedValue,
  setExpandedValue,
  editingValue,
  setEditingValue,
  editValue,
  setEditValue,
  editDescription,
  setEditDescription,
  editNature,
  setEditNature,
  editProprietaireNom,
  setEditProprietaireNom,
  editProprietaireType,
  setEditProprietaireType,
  handleStartEdit,
  handleEditSave,
  handleCancelEdit,
  handleRemoveBusinessValue,
  handleToggleSecurityPillar,
  // Support asset props
  newSupportAssetName,
  setNewSupportAssetName,
  newSupportAssetDescription,
  setNewSupportAssetDescription,
  newSupportAssetType,
  setNewSupportAssetType,
  newSupportAssetOwner,
  setNewSupportAssetOwner,
  newSupportAssetLocation,
  setNewSupportAssetLocation,
  // CPE fields
  newSupportAssetVendor,
  setNewSupportAssetVendor,
  newSupportAssetProduct,
  setNewSupportAssetProduct,
  newSupportAssetVersion,
  setNewSupportAssetVersion,
  discoveredCPE,
  cpeConfidence,
  cpeSearching,
  assetTypes,
  handleAddSupportAsset,
  editingAsset,
  // For support assets, only require businessValueId since child ID exists
  handleStartEditSupportAsset,
  handleCancelEditSupportAsset,
  handleEditSupportAssetChange,
  handleEditSupportAssetSave,
  handleRemoveSupportAsset,
  handleGlobalDeleteSupportAsset,
  allExistingSupportAssets,
  handleLinkExistingSupportAsset
}) => {
  const { t } = useTranslation();

  const [showAddAssetForm, setShowAddAssetForm] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    type: null, // 'businessValue' or 'supportAsset'
    itemName: '',
    itemId: null,
    businessValueId: null // For support assets
  });

  const [assetCreationMode, setAssetCreationMode] = useState('new'); // 'new' | 'existing'
  const [selectedExistingAssetId, setSelectedExistingAssetId] = useState('');

  const isExpanded = expandedValue === value.id;
  const isEditing = editingValue === value.id;
  // Use String comparison to be safe against number/string mismatches
  const isAssetEditing = (assetId) =>
    editingAsset?.businessValueId &&
    String(editingAsset.businessValueId) === String(value.id) &&
    editingAsset.asset?.id === assetId;

  // Safe asset types array
  const safeAssetTypes = assetTypes?.map(type => ({
    value: type.value || type,
    label: type.label || type
  })) || [
      { value: 'organisation', label: 'Organisation' },
      { value: 'locaux', label: 'Locaux' },
      { value: 'equipements', label: 'Équipements' },
      { value: 'reseaux', label: 'Réseaux' },
      { value: 'logiciels', label: 'Logiciels' }
    ];

  // Helper to get icon based on asset type
  const AssetTypeIcon = ({ type }) => {
    switch (type) {
      case 'organisation': return <Users size={16} className="text-orange-500" />;
      case 'locaux': return <Home size={16} className="text-pink-500" />;
      case 'equipements': return <Server size={16} className="text-purple-500" />;
      case 'reseaux': return <Network size={16} className="text-blue-500" />;
      case 'logiciels': return <Code size={16} className="text-green-500" />;
      default: return <Package size={16} className="text-gray-500" />;
    }
  };

  const handleAddAssetForm = async () => {
    if (!(newSupportAssetName || '').trim()) return;

    const assetData = {
      name: newSupportAssetName.trim(),
      description: newSupportAssetDescription?.trim() || '',
      type: newSupportAssetType || 'organisation',
      owner: newSupportAssetOwner?.trim() || '',
      location: newSupportAssetLocation?.trim() || '',
      vendor: newSupportAssetVendor?.trim() || '',
      product: newSupportAssetProduct?.trim() || '',
      version: newSupportAssetVersion?.trim() || ''
    };

    await handleAddSupportAsset(value.id, assetData);

    // Reset form
    setNewSupportAssetName('');
    setNewSupportAssetDescription('');
    setNewSupportAssetType('organisation');
    setNewSupportAssetOwner('');
    setNewSupportAssetLocation('');
    setNewSupportAssetVendor('');
    setNewSupportAssetProduct('');
    setNewSupportAssetVersion('');
  };

  const currentVMAssetIds = (value.supportAssets || []).map(a => a.id);
  const availableExistingAssets = (allExistingSupportAssets || []).filter(a => !currentVMAssetIds.includes(a.id));

  const handleLinkAssetForm = () => {
    if (!selectedExistingAssetId) return;
    const existingAsset = availableExistingAssets.find(a => a.id === selectedExistingAssetId);
    if (existingAsset) {
      handleLinkExistingSupportAsset(value.id, existingAsset);
      setSelectedExistingAssetId('');
      setShowAddAssetForm(false);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
    >
      {/* Business Value Header */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            {/* Expand/Collapse Button */}
            <button
              onClick={() => setExpandedValue(isExpanded ? null : value.id)}
              className="text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>

            {/* Business Value ID and Name */}
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 text-xs font-semibold text-indigo-700 bg-indigo-100 rounded-md">
                {value.shortId || `VM${value.id}`}
              </span>
              {isEditing ? (
                <input
                  type="text"
                  value={editValue || ''}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="px-2 py-1 border rounded text-sm font-medium"
                  placeholder={t('businessValues.fields.businessValueName')}
                />
              ) : (
                <span className="font-medium text-gray-900">{value.name}</span>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1">
            {isEditing ? (
              <>
                <button
                  onClick={() => handleEditSave(value.id)}
                  disabled={!editValue.trim()}
                  className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors disabled:opacity-50"
                  title={t('businessValues.buttons.save')}
                >
                  <Save size={16} />
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded transition-colors"
                  title={t('businessValues.buttons.cancel')}
                >
                  <X size={16} />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => handleStartEdit(value)}
                  className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                  title={t('businessValues.buttons.edit')}
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => {
                    setDeleteConfirmation({
                      isOpen: true,
                      type: 'businessValue',
                      itemName: value.name,
                      itemId: value.id,
                      businessValueId: null
                    });
                  }}
                  className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                  title={t('businessValues.buttons.delete')}
                >
                  <Trash2 size={16} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="mt-2">
          {isEditing ? (
            <textarea
              value={editDescription || ''}
              onChange={(e) => setEditDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              rows={2}
              placeholder={t('businessValues.fields.businessValueDescription')}
            />
          ) : (
            value.description && (
              <p className="text-sm text-gray-600 ml-8">{value.description}</p>
            )
          )}
        </div>

        {/* Nature & Proprietaire - Edit Mode */}
        {isEditing && (
          <div className="mt-2 ml-8 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('businessValues.fields.nature')}
              </label>
              <select
                value={editNature}
                onChange={(e) => setEditNature(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white"
              >
                <option value="processus">{t('businessValues.fields.natureProcessus')}</option>
                <option value="information">{t('businessValues.fields.natureInformation')}</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('businessValues.fields.proprietaireNom')}
              </label>
              <input
                type="text"
                value={editProprietaireNom}
                onChange={(e) => setEditProprietaireNom(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                placeholder={t('businessValues.fields.proprietaireNom')}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('businessValues.fields.proprietaireType')}
              </label>
              <select
                value={editProprietaireType}
                onChange={(e) => setEditProprietaireType(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white"
              >
                <option value="interne">{t('businessValues.fields.proprietaireInterne')}</option>
                <option value="externe">{t('businessValues.fields.proprietaireExterne')}</option>
              </select>
            </div>
          </div>
        )}

        {/* Nature & Proprietaire - Display Mode */}
        {!isEditing && (
          <div className="mt-2 ml-8 flex flex-wrap gap-4 text-xs text-gray-500">
            {value.nature && (
              <span className="flex items-center gap-1">
                <span className="font-medium text-gray-700">{t('businessValues.fields.nature')} :</span>
                <span className={`px-1.5 py-0.5 rounded ${
                  value.nature === 'processus' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                }`}>
                  {t('businessValues.fields.nature' + (value.nature === 'processus' ? 'Processus' : 'Information'))}
                </span>
              </span>
            )}
            {(value.proprietaire?.nom || value.proprietaire?.type) && (
              <span className="flex items-center gap-1">
                <span className="font-medium text-gray-700">{t('businessValues.fields.proprietaireNom')} :</span>
                {value.proprietaire.nom && <span>{value.proprietaire.nom}</span>}
                {value.proprietaire.type && (
                  <span className={`px-1.5 py-0.5 rounded ${
                    value.proprietaire.type === 'interne' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {t('businessValues.fields.proprietaire' + (value.proprietaire.type === 'interne' ? 'Interne' : 'Externe'))}
                  </span>
                )}
              </span>
            )}
          </div>
        )}

        {/* Security Pillars */}
        <div className="mt-2 flex flex-wrap gap-2 ml-8">
          {SECURITY_PILLARS.map((pillar) => {
            const isSelected = value.securityPillars?.includes(pillar.id);
            return (
              <button
                key={pillar.id}
                onClick={isEditing ? undefined : () => handleToggleSecurityPillar(value.id, pillar.id)}
                className={`px-2 py-1 text-xs rounded-full transition-colors ${isSelected
                  ? 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                  : 'bg-gray-100 text-gray-600 border border-gray-200'
                  } ${isEditing ? 'cursor-not-allowed opacity-50' : 'hover:shadow-sm'}`}
                disabled={isEditing}
              >
                {t(`securityPillars.${pillar.id}`)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Expandable Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-gray-200 bg-gray-50"
          >
            <div className="p-4">
              {/* Support Assets Section */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900 flex items-center">
                    <Package size={18} className="mr-2 text-gray-600" />
                    {t('businessValues.sections.supportAssets')}
                  </h4>
                  <div className="flex items-center gap-2">
                    {value.supportAssets?.length > 0 && (
                      <span className="text-sm text-gray-500">
                        {value.supportAssets.length} {t('businessValues.assets')}
                      </span>
                    )}
                    <button
                      onClick={() => setShowAddAssetForm(!showAddAssetForm)}
                      className="bg-indigo-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-indigo-700 flex items-center gap-1.5"
                    >
                      <Plus size={14} />
                      {showAddAssetForm ? t('common.cancel') || 'Annuler' : t('businessValues.buttons.addAsset') || 'Ajouter'}
                    </button>
                  </div>
                </div>

                {/* Add New Asset Form - Only show when toggle is active */}
                <AnimatePresence>
                  {showAddAssetForm && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="bg-white p-3 rounded-md border border-gray-200 mb-4">
                        <h5 className="font-medium text-gray-800 mb-3 flex items-center justify-between">
                          <span className="flex items-center">
                            <Plus size={16} className="mr-2" />
                            {t('businessValues.sections.addSupportAsset')}
                          </span>
                          <div className="flex bg-gray-100 p-1 rounded-md">
                            <button
                              onClick={() => setAssetCreationMode('new')}
                              className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${assetCreationMode === 'new' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                              Créer un nouveau
                            </button>
                            <button
                              onClick={() => setAssetCreationMode('existing')}
                              className={`px-3 py-1 text-xs font-medium rounded-sm transition-colors ${assetCreationMode === 'existing' ? 'bg-white shadow-sm text-indigo-700' : 'text-gray-500 hover:text-gray-700'}`}
                            >
                              Lier un existant
                            </button>
                          </div>
                        </h5>

                        {assetCreationMode === 'new' ? (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t('businessValues.fields.supportAssetName')}
                                </label>
                                <input
                                  type="text"
                                  value={newSupportAssetName}
                                  onChange={(e) => setNewSupportAssetName(e.target.value)}
                                  placeholder={t('businessValues.fields.supportAssetName')}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Nature
                                </label>
                                <select
                                  value={newSupportAssetType}
                                  onChange={(e) => setNewSupportAssetType(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                                >
                                  {safeAssetTypes.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t('businessValues.fields.owner')}
                                </label>
                                <input
                                  type="text"
                                  value={newSupportAssetOwner}
                                  onChange={(e) => setNewSupportAssetOwner(e.target.value)}
                                  placeholder={t('businessValues.fields.owner')}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  {t('businessValues.fields.location')}
                                </label>
                                <input
                                  type="text"
                                  value={newSupportAssetLocation}
                                  onChange={(e) => setNewSupportAssetLocation(e.target.value)}
                                  placeholder={t('businessValues.fields.location')}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                                />
                              </div>
                            </div>
                            <div className="mb-3">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                {t('businessValues.fields.supportAssetDescription')}
                              </label>
                              <textarea
                                value={newSupportAssetDescription}
                                onChange={(e) => setNewSupportAssetDescription(e.target.value)}
                                placeholder={t('businessValues.fields.supportAssetDescription')}
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                              />
                            </div>

                            {/* CPE Fields - Show only for technical assets */}
                            {['equipements', 'reseaux', 'logiciels'].includes(newSupportAssetType) && (
                              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                <div className="flex items-center mb-2">
                                  <Code size={16} className="text-blue-600 mr-2" />
                                  <h6 className="text-sm font-medium text-blue-900">
                                    Informations techniques (pour l'analyse CTI)
                                  </h6>
                                </div>
                                <p className="text-xs text-blue-700 mb-3">
                                  Ces informations permettent une recherche précise des vulnérabilités dans les bases NIST et EU-VD
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Éditeur / Vendor
                                      <span className="text-blue-600 ml-1">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={newSupportAssetVendor}
                                      onChange={(e) => setNewSupportAssetVendor(e.target.value)}
                                      placeholder="Ex: microsoft, oracle, cisco"
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                                    />
                                    <p className="text-xs text-gray-500 mt-0.5">Nom de l'éditeur</p>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Produit / Product
                                      <span className="text-blue-600 ml-1">*</span>
                                    </label>
                                    <input
                                      type="text"
                                      value={newSupportAssetProduct}
                                      onChange={(e) => setNewSupportAssetProduct(e.target.value)}
                                      placeholder="Ex: windows_server, mysql, ios"
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                                    />
                                    <p className="text-xs text-gray-500 mt-0.5">Nom du produit</p>
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Version
                                    </label>
                                    <input
                                      type="text"
                                      value={newSupportAssetVersion}
                                      onChange={(e) => setNewSupportAssetVersion(e.target.value)}
                                      placeholder="Ex: 2019, 8.0, 15.2"
                                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                                    />
                                    <p className="text-xs text-gray-500 mt-0.5">Version (optionnel)</p>
                                  </div>
                                </div>
                                {cpeSearching && (
                                  <div className="mt-2 text-xs text-blue-600 flex items-center">
                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600 mr-2"></div>
                                    Recherche CPE en cours...
                                  </div>
                                )}
                                {discoveredCPE && (
                                  <div className="mt-2 p-2 bg-white rounded border border-blue-300">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-xs font-medium text-blue-800">CPE Généré</span>
                                      {cpeConfidence && cpeConfidence !== 'Generated' && (
                                        <span className={`text-xs px-1.5 py-0.5 rounded ${cpeConfidence === 'High' ? 'bg-green-100 text-green-700' :
                                          'bg-gray-100 text-gray-700'
                                          }`}>
                                          {cpeConfidence === 'High' ? 'Vérifié' : cpeConfidence}
                                        </span>
                                      )}
                                    </div>
                                    <code className="text-xs bg-gray-50 p-1 rounded block font-mono text-gray-700 break-all">
                                      {discoveredCPE}
                                    </code>
                                  </div>
                                )}
                              </div>
                            )}

                            <button
                              onClick={() => {
                                handleAddAssetForm();
                                setShowAddAssetForm(false);
                              }}
                              disabled={!newSupportAssetName?.trim()}
                              className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Plus size={16} className="inline mr-2" />
                              {t('businessValues.buttons.addAsset', 'Ajouter un nouveau')}
                            </button>
                          </>
                        ) : (
                          <>
                            <div className="mb-4">
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Sélectionner un bien support existant
                              </label>
                              {availableExistingAssets.length === 0 ? (
                                <p className="text-sm text-gray-500 italic p-2 border border-dashed border-gray-300 rounded-md bg-gray-50">
                                  Aucun bien support existant disponible pour être lié à cette valeur métier.
                                </p>
                              ) : (
                                <select
                                  value={selectedExistingAssetId}
                                  onChange={(e) => setSelectedExistingAssetId(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white"
                                >
                                  <option value="">Sélectionnez un bien support...</option>
                                  {availableExistingAssets.map(asset => (
                                    <option key={asset.id} value={asset.id}>
                                      {asset.name} - ({safeAssetTypes.find(t => t.value === asset.type)?.label || asset.type})
                                    </option>
                                  ))}
                                </select>
                              )}
                            </div>
                            <button
                              onClick={handleLinkAssetForm}
                              disabled={!selectedExistingAssetId}
                              className="bg-indigo-600 text-white px-4 py-2 rounded-md text-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Plus size={16} className="inline mr-2" />
                              Lier le bien support existant
                            </button>
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Assets List */}
                {value.supportAssets?.length > 0 ? (
                  <div className="bg-white rounded-md border border-gray-200 divide-y divide-gray-100 shadow-sm">
                    {(value.supportAssets || []).map(asset => (
                      <motion.div
                        key={asset.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="p-3 hover:bg-gray-50"
                      >
                        {isAssetEditing(asset.id) ? (
                          // --- Inline Edit Form for Asset ---
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {/* Edit Name */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  {t('businessValues.fields.supportAssetName')}
                                </label>
                                <input
                                  type="text"
                                  value={editingAsset?.asset?.name || ''}
                                  onChange={(e) => handleEditSupportAssetChange('name', e.target.value)}
                                  placeholder={t('businessValues.fields.supportAssetName')}
                                  className="w-full p-2 border rounded text-sm"
                                />
                              </div>
                              {/* Edit Type */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Nature
                                </label>
                                <select
                                  value={editingAsset?.asset?.type || ''}
                                  onChange={(e) => handleEditSupportAssetChange('type', e.target.value)}
                                  className="w-full p-2 border rounded text-sm bg-white"
                                >
                                  {safeAssetTypes.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
                                </select>
                              </div>
                              {/* Edit Owner */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  {t('businessValues.fields.owner')}
                                </label>
                                <input
                                  type="text"
                                  value={editingAsset?.asset?.owner || ''}
                                  onChange={(e) => handleEditSupportAssetChange('owner', e.target.value)}
                                  placeholder={t('businessValues.fields.owner')}
                                  className="w-full p-2 border rounded text-sm"
                                />
                              </div>
                              {/* Edit Location */}
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  {t('businessValues.fields.location')}
                                </label>
                                <input
                                  type="text"
                                  value={editingAsset?.asset?.location || ''}
                                  onChange={(e) => handleEditSupportAssetChange('location', e.target.value)}
                                  placeholder={t('businessValues.fields.location')}
                                  className="w-full p-2 border rounded text-sm"
                                />
                              </div>
                            </div>
                            {/* Edit Description */}
                            <textarea
                              value={editingAsset?.asset?.description || ''}
                              onChange={(e) => handleEditSupportAssetChange('description', e.target.value)}
                              placeholder={t('businessValues.fields.description')}
                              rows="2"
                              className="w-full p-2 border rounded text-sm"
                            />

                            {/* CPE Fields for Edit - Show only for technical assets */}
                            {['equipements', 'reseaux', 'logiciels'].includes(editingAsset?.asset?.type) && (
                              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                                <div className="flex items-center mb-2">
                                  <Code size={14} className="text-blue-600 mr-1.5" />
                                  <span className="text-xs font-medium text-blue-900">
                                    Informations techniques (CTI)
                                  </span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Éditeur
                                    </label>
                                    <input
                                      type="text"
                                      value={editingAsset?.asset?.vendor || ''}
                                      onChange={(e) => handleEditSupportAssetChange('vendor', e.target.value)}
                                      placeholder="Ex: microsoft"
                                      className="w-full p-1.5 border rounded text-xs"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Produit
                                    </label>
                                    <input
                                      type="text"
                                      value={editingAsset?.asset?.product || ''}
                                      onChange={(e) => handleEditSupportAssetChange('product', e.target.value)}
                                      placeholder="Ex: windows_server"
                                      className="w-full p-1.5 border rounded text-xs"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Version
                                    </label>
                                    <input
                                      type="text"
                                      value={editingAsset?.asset?.version || ''}
                                      onChange={(e) => handleEditSupportAssetChange('version', e.target.value)}
                                      placeholder="Ex: 2019"
                                      className="w-full p-1.5 border rounded text-xs"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Edit Actions */}
                            <div className="flex justify-end gap-2">
                              <button
                                onClick={handleEditSupportAssetSave}
                                className="bg-green-500 text-white px-2 py-1 rounded text-xs disabled:opacity-50"
                                disabled={!(editingAsset?.asset?.name || '').trim()}
                              >
                                <Save size={14} className="inline mr-1" />{t('businessValues.buttons.save')}
                              </button>
                              <button
                                onClick={handleCancelEditSupportAsset}
                                className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs"
                              >
                                <X size={14} className="inline mr-1" />{t('businessValues.buttons.cancel')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          // --- Display Asset ---
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-grow">
                              <div className="flex items-center mb-1">
                                <span className="mr-2 px-1.5 py-0.5 text-xs rounded-full text-blue-700 bg-blue-100 border border-blue-200 font-mono">
                                  {asset.shortId}
                                </span>
                                <span className="font-medium text-gray-800 mr-2">{asset.name}</span>
                                <span className="text-xs text-gray-500 flex items-center gap-1">
                                  <AssetTypeIcon type={asset.type} />
                                  {safeAssetTypes.find(t => t.value === asset.type)?.label || asset.type}
                                </span>
                              </div>
                              {asset.description && <p className="text-sm text-gray-600 mb-1 ml-1 pl-2 border-l-2 border-gray-200">{asset.description}</p>}

                              {/* CPE Information */}
                              {asset.cpe && (
                                <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-medium text-blue-800 flex items-center">
                                      <Code size={12} className="mr-1" />
                                      Identifiant technique
                                    </span>
                                    {asset.cpeConfidence && asset.cpeConfidence !== 'Generated' && (
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${asset.cpeConfidence === 'High' ? 'bg-green-100 text-green-700' :
                                        'bg-gray-100 text-gray-700'
                                        }`}>
                                        {asset.cpeConfidence === 'High' ? 'Vérifié' : asset.cpeConfidence}
                                      </span>
                                    )}
                                  </div>
                                  {(asset.vendor || asset.product || asset.version) && (
                                    <div className="text-xs text-blue-700 mb-1">
                                      {asset.vendor && <span className="mr-2">📦 {asset.vendor}</span>}
                                      {asset.product && <span className="mr-2">🔧 {asset.product}</span>}
                                      {asset.version && <span>📋 v{asset.version}</span>}
                                    </div>
                                  )}
                                  <code className="text-xs bg-white p-1 rounded block font-mono text-gray-600 break-all border">
                                    {asset.cpe}
                                  </code>
                                </div>
                              )}

                              <div className="text-xs text-gray-500 ml-1 flex flex-wrap gap-x-3 gap-y-1 mt-2">
                                {asset.owner && <span className="flex items-center"><Users size={12} className="mr-1" /> {asset.owner}</span>}
                                {asset.location && <span className="flex items-center"><MapPin size={12} className="mr-1" /> {asset.location}</span>}
                              </div>
                            </div>
                            {/* Asset Actions */}
                            <div className="flex flex-col sm:flex-row gap-1 flex-shrink-0">
                              <button
                                onClick={() => handleStartEditSupportAsset(value.id, asset)}
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1 rounded text-xs"
                                title={t('businessValues.buttons.edit', 'Éditer')}
                              >
                                <Edit2 size={14} />
                              </button>
                              <button
                                onClick={() => {
                                  setDeleteConfirmation({
                                    isOpen: true,
                                    type: 'unlinkSupportAsset',
                                    itemName: asset.name,
                                    itemId: asset.id,
                                    businessValueId: value.id
                                  });
                                }}
                                className="text-orange-500 hover:text-orange-700 hover:bg-orange-50 p-1 rounded text-xs"
                                title={t('businessValues.buttons.unlink', 'Délier de cette valeur métier')}
                              >
                                <Unlink size={14} />
                              </button>
                              <button
                                onClick={() => {
                                  setDeleteConfirmation({
                                    isOpen: true,
                                    type: 'supportAsset',
                                    itemName: asset.name,
                                    itemId: asset.id,
                                    businessValueId: value.id
                                  });
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded text-xs"
                                title={t('businessValues.buttons.delete', 'Supprimer de toutes les valeurs métiers')}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Package size={48} className="mx-auto mb-3 text-gray-300" />
                    <p>{t('businessValues.empty.noAssets')}</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ ...deleteConfirmation, isOpen: false })}
        onConfirm={() => {
          if (deleteConfirmation.type === 'businessValue') {
            handleRemoveBusinessValue(deleteConfirmation.itemId);
          } else if (deleteConfirmation.type === 'supportAsset') {
            // Global Delete
            handleGlobalDeleteSupportAsset(deleteConfirmation.itemId);
          } else if (deleteConfirmation.type === 'unlinkSupportAsset') {
            // Local Unlink
            handleRemoveSupportAsset(deleteConfirmation.businessValueId, deleteConfirmation.itemId);
          }
        }}
        itemName={deleteConfirmation.itemName}
        itemType={deleteConfirmation.type}
      />
    </motion.div>
  );
};

export default BusinessValueListItem;

